import React, { lazy, Suspense } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { Toaster } from "sonner";

import AppShell from "./components/layout/AppShell";
import Setup from "./pages/Setup";
import Login from "./pages/Login";
import Recover from "./pages/Recover";
import Dashboard from "./pages/Dashboard";
import ClientsList from "./pages/Clients/ClientsList";
import ClientForm from "./pages/Clients/ClientForm";
import ProposalsList from "./pages/Proposals/ProposalsList";
import NewProposal from "./pages/Proposals/NewProposal";
import PublicProposal from "./pages/Proposals/PublicProposal";
import PREForm from "./pages/Documents/PREForm";
import DocumentsList from "./pages/Documents/DocumentsList";
import FixedEvaluations from "./pages/Catalog/FixedEvaluations";
import ChemicalsCatalog from "./pages/Catalog/ChemicalsCatalog";
import Services from "./pages/Catalog/Services";
import Cidades from "./pages/Catalog/Cidades";
import Settings from "./pages/Settings/Settings";
import Integrations from "./pages/Integrations";
import McpDiagnostics from "./pages/McpDiagnostics";
import Cenarios from "./pages/Cenarios";
import TreinamentosPage from "./pages/Treinamentos/TreinamentosPage";
import VencimentosPage from "./pages/Treinamentos/VencimentosPage";
import TrainingAuditPage from "./pages/Treinamentos/TrainingAuditPage";
import PublicCertificate from "./pages/Treinamentos/PublicCertificate";
import EmissionsHistory from "./pages/Treinamentos/EmissionsHistory";
import OperationsHub from "./pages/Operations/OperationsHub";
import OperationsQueue from "./pages/Operations/OperationsQueue";
import ObligationsPage from "./pages/Operations/ObligationsPage";
import DocumentIntelligence from "./pages/Operations/DocumentIntelligence";
import EfficiencyAnalytics from "./pages/Operations/EfficiencyAnalytics";
import SmartAgenda from "./pages/Operations/SmartAgenda";
import TechnicalLibrary from "./pages/Operations/TechnicalLibrary";
import TodayCEO from "./pages/Operations/TodayCEO";
import Client360 from "./pages/Operations/Client360";
import PublicActions from "./pages/Operations/PublicActions";
import VisitExecution from "./pages/Operations/VisitExecution";

const FireProtection = lazy(() => import("./pages/Fire/FireProtection"));
const FirePublic = lazy(() => import("./pages/Fire/FirePublic"));
const fireLoading = <div className="min-h-screen grid place-items-center">Carregando proteção contra incêndio…</div>;

function ProtectedShell({ children, adminOnly = false }) {
  const { token: ctxToken, loading, setupRequired, isAdmin, isCollaborator } = useAuth();
  const location = useLocation();
  const token = ctxToken || localStorage.getItem("ceo_token");
  if (loading) return <div className="min-h-screen grid place-items-center text-muted-foreground">Carregando...</div>;
  if (setupRequired) return <Navigate to="/setup" replace state={{ from: location }} />;
  if (!token) return <Navigate to="/login" replace state={{ from: location }} />;
  if (adminOnly && !isAdmin && isCollaborator) return <Navigate to="/" replace />;
  return <AppShell>{children}</AppShell>;
}

function PublicRoute({ children, mode }) {
  const { token: ctxToken, setupRequired, loading } = useAuth();
  const token = ctxToken || localStorage.getItem("ceo_token");
  if (loading) return <div className="min-h-screen grid place-items-center text-muted-foreground">Carregando...</div>;
  if (mode === "setup" && !setupRequired) return <Navigate to="/login" replace />;
  if (mode === "login" && setupRequired) return <Navigate to="/setup" replace />;
  if (mode === "login" && token) return <Navigate to="/" replace />;
  return children;
}

function AppRoutes() {
  const [theme, setTheme] = React.useState(() => localStorage.getItem("ceo_theme") || "dark");
  React.useEffect(() => {
    const t = localStorage.getItem("ceo_theme") || "dark";
    if (t === "dark") document.documentElement.classList.add("dark");
    const obs = new MutationObserver(() => {
      setTheme(document.documentElement.classList.contains("dark") ? "dark" : "light");
    });
    obs.observe(document.documentElement, { attributes: true, attributeFilter: ["class"] });
    return () => obs.disconnect();
  }, []);

  return (
    <>
      <Routes>
        <Route path="/incendio" element={<ProtectedShell><Suspense fallback={fireLoading}><FireProtection /></Suspense></ProtectedShell>} />
        <Route path="/incendio/e/:token" element={<Suspense fallback={fireLoading}><FirePublic /></Suspense>} />
        <Route path="/incendio/cliente/:token" element={<Suspense fallback={fireLoading}><FirePublic portal /></Suspense>} />
        <Route path="/proposta/:token" element={<PublicProposal />} />
        <Route path="/certificado" element={<PublicCertificate />} />
        <Route path="/certificado/:code" element={<PublicCertificate />} />
        <Route path="/cliente/acoes/:token" element={<PublicActions />} />

        <Route path="/setup" element={<PublicRoute mode="setup"><Setup /></PublicRoute>} />
        <Route path="/login" element={<PublicRoute mode="login"><Login /></PublicRoute>} />
        <Route path="/recover" element={<PublicRoute mode="login"><Recover /></PublicRoute>} />

        <Route path="/" element={<ProtectedShell><Dashboard /></ProtectedShell>} />
        <Route path="/operacoes/hoje" element={<ProtectedShell><TodayCEO /></ProtectedShell>} />
        <Route path="/operacoes" element={<ProtectedShell><OperationsHub /></ProtectedShell>} />
        <Route path="/operacoes/fila" element={<ProtectedShell><OperationsQueue /></ProtectedShell>} />
        <Route path="/operacoes/agenda" element={<ProtectedShell><SmartAgenda /></ProtectedShell>} />
        <Route path="/operacoes/biblioteca" element={<ProtectedShell><TechnicalLibrary /></ProtectedShell>} />
        <Route path="/operacoes/obrigacoes" element={<ProtectedShell><ObligationsPage /></ProtectedShell>} />
        <Route path="/operacoes/documentos" element={<ProtectedShell><DocumentIntelligence /></ProtectedShell>} />
        <Route path="/operacoes/indicadores" element={<ProtectedShell><EfficiencyAnalytics /></ProtectedShell>} />
        <Route path="/operacoes/visitas/:id" element={<ProtectedShell><VisitExecution /></ProtectedShell>} />
        <Route path="/clientes" element={<ProtectedShell><ClientsList /></ProtectedShell>} />
        <Route path="/clientes/:id/360" element={<ProtectedShell><Client360 /></ProtectedShell>} />
        <Route path="/clientes/:id" element={<ProtectedShell><ClientForm /></ProtectedShell>} />

        <Route path="/propostas" element={<ProtectedShell><ProposalsList /></ProtectedShell>} />
        <Route path="/propostas/nova" element={<ProtectedShell><NewProposal /></ProtectedShell>} />
        <Route path="/propostas/:id" element={<ProtectedShell><NewProposal /></ProtectedShell>} />

        <Route path="/documentos/pre" element={<ProtectedShell adminOnly><PREForm /></ProtectedShell>} />
        <Route path="/documentos" element={<ProtectedShell><DocumentsList /></ProtectedShell>} />

        <Route path="/cenarios" element={<ProtectedShell adminOnly><Cenarios /></ProtectedShell>} />
        <Route path="/treinamentos" element={<ProtectedShell><TreinamentosPage /></ProtectedShell>} />
        <Route path="/treinamentos/emissoes" element={<ProtectedShell><EmissionsHistory /></ProtectedShell>} />
        <Route path="/treinamentos/vencimentos" element={<ProtectedShell><VencimentosPage /></ProtectedShell>} />
        <Route path="/treinamentos/auditoria" element={<ProtectedShell><TrainingAuditPage /></ProtectedShell>} />

        <Route path="/cadastros/avaliacoes-fixas" element={<ProtectedShell adminOnly><FixedEvaluations /></ProtectedShell>} />
        <Route path="/cadastros/avaliacoes-quimicas" element={<ProtectedShell adminOnly><ChemicalsCatalog /></ProtectedShell>} />
        <Route path="/cadastros/servicos" element={<ProtectedShell adminOnly><Services /></ProtectedShell>} />
        <Route path="/cadastros/cidades" element={<ProtectedShell adminOnly><Cidades /></ProtectedShell>} />
        <Route path="/integracoes" element={<ProtectedShell><Integrations /></ProtectedShell>} />
        <Route path="/mcp/diagnostico" element={<ProtectedShell><McpDiagnostics /></ProtectedShell>} />
        <Route path="/configuracoes" element={<ProtectedShell adminOnly><Settings /></ProtectedShell>} />
        <Route path="*" element={<RoleFallback />} />
      </Routes>
      <Toaster position="top-right" richColors theme={theme} />
    </>
  );
}

function RoleFallback() {
  return <Navigate to="/" replace />;
}

export default function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <AppRoutes />
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}

